import React, { useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Header from "../../components/content_wrapper/header";
import { useGetCronJobs } from "../../hooks/cronJobs/useGetCronJobs";
import { useExecuteCronJob } from "../../hooks/cronJobs/useExecuteCronJob";

const fmtDate = (d: any) => {
  if (!d) return "—";
  const m = typeof d === "number" ? moment.unix(d) : moment(d);
  return m.isValid() ? m.format("MMM DD, YYYY h:mm A") : "—";
};

const fromNow = (d: any) => {
  if (!d) return "";
  const m = typeof d === "number" ? moment.unix(d) : moment(d);
  return m.isValid() ? m.fromNow() : "";
};

// A job is "running" while locked_at is set AND the lease (timeout + 2 min
// margin, mirroring the scheduler) has not expired. A lock older than that is a
// crashed run the scheduler will reclaim, not a live one.
const LEASE_MARGIN_MS = 120000;
const isRunning = (job: any) => {
  if (!job?.lockedAt) return false;
  const lockedMs = moment(job.lockedAt).valueOf();
  if (!lockedMs) return false;
  const leaseMs = (job.timeout || 300000) + LEASE_MARGIN_MS;
  return Date.now() - lockedMs < leaseMs;
};

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    success: "bg-success",
    failed: "bg-danger",
    pending: "bg-secondary",
  };
  return map[status] || "bg-secondary";
};

const CronJobsScreen: React.FC = () => {
  const { data, isLoading, isFetching, refetch } = useGetCronJobs({ limit: 100 });
  const { mutate: execute } = useExecuteCronJob();
  const [runningId, setRunningId] = useState<string | null>(null);

  const jobs: any[] = data?.data?.cronJobs || [];

  const runJob = (job: any, force: boolean) => {
    setRunningId(job._id);
    execute(
      { id: job._id, force },
      {
        onSuccess: (res: any) => {
          const ms = res?.data?.executionTime;
          toast.success(
            `"${job.name}" ran successfully${ms ? ` in ${(ms / 1000).toFixed(1)}s` : ""}`
          );
          refetch();
        },
        onError: (error: any) => {
          const status = error?.response?.status;

          // 409 - the scheduler (or another manual run) is already running it.
          if (status === 409) {
            const lockedAt = error?.response?.data?.data?.lockedAt;
            Swal.fire({
              title: "Job already running",
              html:
                (error?.response?.data?.message ||
                  "This job is already running.") +
                (lockedAt
                  ? `<br/><br/><small class="text-muted">Started ${fromNow(
                      lockedAt
                    )}</small>`
                  : ""),
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#d33",
              cancelButtonColor: "#72bcb8",
              confirmButtonText: "Force run anyway",
              cancelButtonText: "Cancel",
            }).then((result) => {
              if (result.isConfirmed) {
                runJob(job, true);
              }
            });
            return;
          }

          // No response = the client timed out (30s) waiting on a long job that
          // is still running server-side. Not a real failure.
          if (!error?.response) {
            toast.info(
              `"${job.name}" is still running on the server (the request timed out waiting for it). Refresh in a bit to see the result.`
            );
            setTimeout(() => refetch(), 4000);
            return;
          }

          toast.error(
            error?.displayMessage ||
              error?.response?.data?.message ||
              `Failed to run "${job.name}"`
          );
        },
        onSettled: () => {
          setRunningId(null);
        },
      }
    );
  };

  return (
    <div className="content-wrapper">
      <Header title="Cron Jobs" />
      <div className="main-wrapper px-4 pt-4 pb-4">
        <div className="wrapper-card mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted small mb-0">
              Background scheduler jobs. Each runs on its own schedule; use
              <strong> Run now </strong> to trigger one immediately. A job that is
              already running (scheduled run or another manual trigger) returns a
              conflict — you can then choose to force it.
            </p>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <i className="fa-solid fa-rotate-right me-1" />
              Refresh
            </button>
          </div>

          <div className="table-blk">
            <div className="table-responsive">
              <table className="display border-0 w-100">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Schedule</th>
                    <th>Enabled</th>
                    <th>Last Run</th>
                    <th>Status</th>
                    <th>Next Run</th>
                    <th>Runs / Fails</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job: any) => {
                    const running = isRunning(job);
                    const busy = runningId === job._id;
                    return (
                      <tr key={job._id}>
                        <td>
                          <div className="fw-semibold">{job.name}</div>
                          {job.description && (
                            <div className="text-muted small">
                              {job.description}
                            </div>
                          )}
                        </td>
                        <td>
                          <code>{job.schedule}</code>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              job.enabled ? "bg-success" : "bg-secondary"
                            }`}
                          >
                            {job.enabled ? "Enabled" : "Disabled"}
                          </span>
                        </td>
                        <td className="small">
                          {fmtDate(job.lastRun)}
                          {job.lastRun && (
                            <div className="text-muted">
                              {fromNow(job.lastRun)}
                            </div>
                          )}
                        </td>
                        <td>
                          {running ? (
                            <span className="badge bg-info text-dark">
                              Running…
                            </span>
                          ) : job.lastRunStatus ? (
                            <span
                              className={`badge ${statusBadge(
                                job.lastRunStatus
                              )}`}
                              title={job.lastRunMessage || ""}
                            >
                              {job.lastRunStatus}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="small">{fmtDate(job.nextRun)}</td>
                        <td className="small">
                          {job.runCount ?? 0}
                          {" / "}
                          <span
                            className={
                              job.failureCount ? "text-danger fw-semibold" : ""
                            }
                          >
                            {job.failureCount ?? 0}
                          </span>
                          {job.consecutiveFailures > 0 && (
                            <div
                              className={
                                job.consecutiveFailures >=
                                (job.consecutiveFailureAlertThreshold ?? 3)
                                  ? "text-danger fw-semibold"
                                  : "text-muted"
                              }
                              title={`${job.consecutiveFailures} consecutive failure(s); alert threshold ${
                                job.consecutiveFailureAlertThreshold ?? 3
                              }`}
                            >
                              ⚠ {job.consecutiveFailures} in a row
                            </div>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            disabled={busy || runningId !== null}
                            onClick={() => runJob(job, false)}
                          >
                            {busy ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                  aria-hidden="true"
                                />
                                Running…
                              </>
                            ) : (
                              "Run now"
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {!isLoading && jobs.length === 0 && (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                <i className="fa-solid fa-clock fa-2x mb-2" />
                <h5 className="mb-1">No cron jobs configured</h5>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CronJobsScreen;
